'use client'

import React, { useCallback, useMemo, useTransition } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getProducts } from '@/lib/actions'
import { useAppSelector, useCheckout, useSearchParams } from '@/lib/hooks'
import { cn } from '@/lib/utils'

import { ProductCard } from '@/components/molecules/product-card'

import { ProductCardHorizontal } from './product-card-horizontal'

interface ProductsGridProps extends React.ComponentPropsWithoutRef<'div'> {}

// Helper function to generate product key - moved outside component for better performance
const generateProductKey = (product: any) =>
  `${product?.id}-${product?.title}-${product?.default_price?.barcode}-${product?.default_price?.price_id}`

export const ProductsGrid = React.memo<ProductsGridProps>(
  ({ className, ...restProps }) => {
    const { productFilterId } = useSearchParams()
    const { productViewMode } = useAppSelector((state) => state.appSession)

    const { data: products, isPending } = useQuery({
      queryKey: ['products', productFilterId],
      queryFn: () => getProducts(productFilterId || undefined),
      refetchInterval: 1000 * 60 * 5, // 5 minutes
    })

    const gridClassName = useMemo(
      () =>
        cn(
          'grid overflow-auto',
          {
            'gap-5 @sm/products:grid-cols-2 @xl/products:grid-cols-3 @4xl/products:grid-cols-4 @5xl/products:grid-cols-5':
              productViewMode === 'vertical',
            'gap-3 @sm/products:grid-cols-1 @3xl/products:grid-cols-2':
              productViewMode === 'horizontal',
          },
          className,
        ),
      [productViewMode, className],
    )

    const renderedProducts = useMemo(() => {
      return (products || [])?.map((product) => {
        const key = generateProductKey(product)

        return productViewMode === 'vertical' ? (
          <ProductCard key={key} product={product} />
        ) : (
          <ProductCardHorizontal key={key} product={product} />
        )
      })
    }, [products, productViewMode])

    return (
      <div className={gridClassName} {...restProps}>
        {isPending && <div>Loading...</div>}
        {!isPending && renderedProducts}
      </div>
    )
  },
)

ProductsGrid.displayName = 'ProductsGrid'
